import React, { useRef, useEffect, useCallback, useLayoutEffect, useState, forwardRef } from 'react';
import { ResizeObserver } from 'resize-observer';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/** Whether runtime is client-side. */


var isClient = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
/**
 * Whether client supports the CSS `object-fit` property.
 */

var supportsCssObjectFit = function supportsCssObjectFit() {
  // Assume object-fit is supported in SSR - it will be picked up again by the
  // client anyway.
  if (!isClient) return true;
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('object-fit', 'cover');
};
/**
 * Stand-alone CSS utility to make replaced elements (`img`, `video`, etc.)
 * fit their container and maintain their aspect ratio.
 */


var styleFitContainer = function styleFitContainer(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$objectFit = _ref.objectFit,
      objectFit = _ref$objectFit === void 0 ? 'cover' : _ref$objectFit,
      _ref$objectPosition = _ref.objectPosition,
      objectPosition = _ref$objectPosition === void 0 ? 'center' : _ref$objectPosition,
      props = _objectWithoutPropertiesLoose(_ref, ["objectFit", "objectPosition"]);

  return _extends({
    display: 'block',
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    objectFit: objectFit,
    objectPosition: objectPosition
  }, props);
};
/** Store the previous supplied value. */


var usePrevious = function usePrevious(value) {
  var ref = useRef(value);
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
/**
 * Event listener binding hook.
 * @param eventName      - Event to bind to
 * @param handler        - Callback handler
 * @param element        - Element to bind to
 * @param handlerOptions - Event handler options
 */


var useEventListener = function useEventListener(eventName, handler, element, handlerOptions) {
  // Create a ref that stores handler
  var savedHandler = useRef(); // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.

  useEffect(function () {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(function () {
    // Make sure element supports addEventListener
    var isSupported = element && element.addEventListener;
    if (!isSupported) return; // Create event listener that calls handler function stored in ref

    var eventListener = function eventListener(event) {
      return savedHandler.current && savedHandler.current(event);
    }; // Add event listener


    element.addEventListener(eventName, eventListener, handlerOptions); // Remove event listener on cleanup

    return function () {
      element.removeEventListener(eventName, eventListener, handlerOptions);
    };
  }, [eventName, element, handlerOptions] // Re-run if eventName or element changes
  );
};
/**
 * Conditionally use `useLayoutEffect` for client *or* `useEffect` for SSR.
 * @see https://github.com/reduxjs/react-redux/blob/c581d480dd675f2645851fb006bef91aeb6ac24d/src/utils/useIsomorphicLayoutEffect.js
 */


var useIsomorphicLayoutEffect = isClient ? useLayoutEffect : useEffect;
/**
 * Bind resize observer callback to element.
 * @param ref       - Element to bind to.
 * @param handler   - Callback for handling entry's bounding rect.
 */

var useResizeObserver = function useResizeObserver(ref, handler) {
  var observer = useRef(new ResizeObserver(function (_ref2) {
    var entry = _ref2[0];
    return handler(entry.contentRect);
  }));
  var observe = useCallback(function () {
    ref.current && observer.current.observe(ref.current);
  }, [ref]); // Bind/rebind observer when `handler` changes.

  useIsomorphicLayoutEffect(function () {
    observer.current = new ResizeObserver(function (_ref3) {
      var entry = _ref3[0];
      return handler(entry.contentRect);
    });
    observe();
    return function () {
      return observer.current.disconnect();
    };
  }, [handler, observe]);
};
/** Handle container to control position. */


var ReactCompareSliderHandleContainer = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      portrait = _ref.portrait;
  var style = {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  };
  var innerStyle = {
    position: 'absolute',
    width: portrait ? '100%' : undefined,
    height: portrait ? undefined : '100%',
    transform: portrait ? 'translateY(-50%)' : 'translateX(-50%)',
    pointerEvents: 'all'
  };
  return React.createElement("div", {
    style: style,
    "data-rcs": "handle-container",
    ref: ref
  }, React.createElement("div", {
    style: innerStyle
  }, children));
});
ReactCompareSliderHandleContainer.displayName = 'ReactCompareSliderHandleContainer';
/** Overridable handle. */

var ReactCompareSliderHandle = function ReactCompareSliderHandle(_ref2) {
  var portrait = _ref2.portrait,
      style = _ref2.style,
      props = _objectWithoutPropertiesLoose(_ref2, ["portrait", "style"]);

  var rootStyle = _extends({
    height: portrait ? 4 : '100%',
    width: portrait ? '100%' : 4,
    backgroundColor: '#fff',
    boxShadow: '0 0 .2rem #000',
    cursor: portrait ? 'ns-resize' : 'ew-resize'
  }, style);

  return React.createElement("div", Object.assign({}, props, {
    style: rootStyle,
    "data-rcs": "handle-inner"
  }));
};
/** Container for items passed to main component. */


var ReactCompareSliderClipContainer = /*#__PURE__*/forwardRef(function (props, ref) {
  var style = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    willChange: 'clip',
    userSelect: 'none',
    KhtmlUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none'
  };
  return React.createElement("div", Object.assign({}, props, {
    style: style,
    "data-rcs": "clip-item",
    ref: ref
  }));
});
ReactCompareSliderClipContainer.displayName = 'ReactCompareSliderClipContainer';
/** Root Comparison slider. */

var ReactCompareSlider = function ReactCompareSlider(_ref3) {
  var handle = _ref3.handle,
      itemOne = _ref3.itemOne,
      itemTwo = _ref3.itemTwo,
      _ref3$onlyHandleDragg = _ref3.onlyHandleDraggable,
      onlyHandleDraggable = _ref3$onlyHandleDragg === void 0 ? false : _ref3$onlyHandleDragg,
      onPositionChange = _ref3.onPositionChange,
      _ref3$portrait = _ref3.portrait,
      portrait = _ref3$portrait === void 0 ? false : _ref3$portrait,
      _ref3$position = _ref3.position,
      position = _ref3$position === void 0 ? 50 : _ref3$position,
      _ref3$boundsPadding = _ref3.boundsPadding,
      boundsPadding = _ref3$boundsPadding === void 0 ? 0 : _ref3$boundsPadding,
      style = _ref3.style,
      props = _objectWithoutPropertiesLoose(_ref3, ["handle", "itemOne", "itemTwo", "onlyHandleDraggable", "onPositionChange", "portrait", "position", "boundsPadding", "style"]);
  /** Reference to root container. */


  var rootContainerRef = useRef(null);
  /** Reference to clip container. */

  var clipContainerRef = useRef(null);
  /** Reference to handle container. */

  var handleContainerRef = useRef(null);
  /** Reference to current position as a percentage value. */

  var internalPositionPc = useRef(position);
  /** Previous `position` prop value. */

  var prevPropPosition = usePrevious(position);
  /** Whether user is currently dragging. */

  var _useState = useState(false),
      isDragging = _useState[0],
      setIsDragging = _useState[1];
  /** Whether component has a `window` event binding. */


  var hasWindowBinding = useRef(false);
  /** Target container for pointer events. */

  var _useState2 = useState(),
      interactiveTarget = _useState2[0],
      setInteractiveTarget = _useState2[1]; // Set target container for pointer events.


  useEffect(function () {
    setInteractiveTarget(onlyHandleDraggable ? handleContainerRef.current : rootContainerRef.current);
  }, [onlyHandleDraggable]);
  /** Update internal position value. */

  var updateInternalPosition = useCallback(function updateInternalCall(_ref4) {
    var x = _ref4.x,
        y = _ref4.y,
        isOffset = _ref4.isOffset,
        _portrait = _ref4.portrait,
        _boundsPadding = _ref4.boundsPadding;

    var _rootContainerRef$cur = rootContainerRef.current.getBoundingClientRect(),
        top = _rootContainerRef$cur.top,
        left = _rootContainerRef$cur.left,
        width = _rootContainerRef$cur.width,
        height = _rootContainerRef$cur.height; // Early out if width or height are zero, can't calculate values
    // from zeros.


    if (width === 0 || height === 0) return; // Clamp pixel position to always be within the container's bounds.
    // This does *not* take `boundsPadding` into account because we need
    // the real coords to correctly position the handle.

    var positionPx = Math.min(Math.max( // Determine bounds based on orientation
    _portrait ? isOffset ? y - top - window.pageYOffset : y : isOffset ? x - left - window.pageXOffset : x, // Min value
    0), // Max value
    _portrait ? height : width); // Calculate internal position percentage *without* bounds - we always
    // want to return 0-100 of the *slideable* bounds.

    var nextInternalPositionPc = positionPx / (_portrait ? height : width) * 100;
    /** Determine if the current pixel position meets the min/max bounds. */

    var positionMeetsBounds = _portrait ? positionPx === 0 || positionPx === height : positionPx === 0 || positionPx === width;
    var canSkipPositionPc = nextInternalPositionPc === internalPositionPc.current && (internalPositionPc.current === 0 || internalPositionPc.current === 100); // Early out if pixel and percentage positions are already at the min/max
    // to prevent update spamming when the user is sliding outside of the
    // container.

    if (canSkipPositionPc && positionMeetsBounds) {
      return;
    } // Set new internal position.


    internalPositionPc.current = nextInternalPositionPc;
    /** Pixel position clamped to extremities *with* bounds padding. */

    var clampedPx = Math.min( // Get largest from pixel position *or* bounds padding.
    Math.max(positionPx, 0 + _boundsPadding), // Use height *or* width based on orientation.
    (_portrait ? height : width) - _boundsPadding); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    clipContainerRef.current.style.clip = _portrait ? "rect(auto,auto," + clampedPx + "px,auto)" : "rect(auto," + clampedPx + "px,auto,auto)"; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    handleContainerRef.current.style.transform = _portrait ? "translate3d(0," + clampedPx + "px,0)" : "translate3d(" + clampedPx + "px,0,0)";
    if (onPositionChange) onPositionChange(internalPositionPc.current);
  }, [onPositionChange]); // Update internal position if `position` prop changes.

  useEffect(function () {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    var _rootContainerRef$cur2 = rootContainerRef.current.getBoundingClientRect(),
        width = _rootContainerRef$cur2.width,
        height = _rootContainerRef$cur2.height; // Use current internal position if `position` hasn't changed.


    var nextPosition = position === prevPropPosition ? internalPositionPc.current : position;
    updateInternalPosition({
      portrait: portrait,
      boundsPadding: boundsPadding,
      x: width / 100 * nextPosition,
      y: height / 100 * nextPosition
    });
  }, [portrait, position, prevPropPosition, boundsPadding, updateInternalPosition]);
  /** Handle mouse/touch down. */

  var handlePointerDown = useCallback(function (ev) {
    ev.preventDefault();
    updateInternalPosition({
      portrait: portrait,
      boundsPadding: boundsPadding,
      isOffset: true,
      x: ev instanceof MouseEvent ? ev.pageX : ev.touches[0].pageX,
      y: ev instanceof MouseEvent ? ev.pageY : ev.touches[0].pageY
    });
    setIsDragging(true);
  }, [portrait, boundsPadding, updateInternalPosition]);
  /** Handle mouse/touch move. */

  var handlePointerMove = useCallback(function moveCall(ev) {
    if (!isDragging) return;
    updateInternalPosition({
      portrait: portrait,
      boundsPadding: boundsPadding,
      isOffset: true,
      x: ev instanceof MouseEvent ? ev.pageX : ev.touches[0].pageX,
      y: ev instanceof MouseEvent ? ev.pageY : ev.touches[0].pageY
    });
  }, [portrait, isDragging, boundsPadding, updateInternalPosition]);
  /** Handle mouse/touch up. */

  var handlePointerUp = useCallback(function () {
    setIsDragging(false);
  }, []);
  /** Resync internal position on resize. */

  var handleResize = useCallback(function (_ref5) {
    var width = _ref5.width,
        height = _ref5.height;
    updateInternalPosition({
      portrait: portrait,
      boundsPadding: boundsPadding,
      x: width / 100 * internalPositionPc.current,
      y: height / 100 * internalPositionPc.current
    });
  }, [portrait, boundsPadding, updateInternalPosition]); // Allow drag outside of container while pointer is still down.

  useEffect(function () {
    if (isDragging && !hasWindowBinding.current) {
      window.addEventListener('mousemove', handlePointerMove, {
        passive: true
      });
      window.addEventListener('mouseup', handlePointerUp, {
        passive: true
      });
      window.addEventListener('touchmove', handlePointerMove, {
        passive: true
      });
      window.addEventListener('touchend', handlePointerUp, {
        passive: true
      });
      hasWindowBinding.current = true;
    }

    return function () {
      if (hasWindowBinding.current) {
        window.removeEventListener('mousemove', handlePointerMove);
        window.removeEventListener('mouseup', handlePointerUp);
        window.removeEventListener('touchmove', handlePointerMove);
        window.removeEventListener('touchend', handlePointerUp);
        hasWindowBinding.current = false;
      }
    };
  }, [handlePointerMove, handlePointerUp, isDragging]); // Bind resize observer to container.

  useResizeObserver(rootContainerRef, handleResize); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

  useEventListener('mousedown', handlePointerDown, interactiveTarget, {
    capture: true,
    passive: false
  }); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

  useEventListener('touchstart', handlePointerDown, interactiveTarget, {
    capture: true,
    passive: false
  }); // Use custom handle if requested.

  var Handle = handle || React.createElement(ReactCompareSliderHandle, {
    portrait: portrait
  });

  var rootStyle = _extends({
    position: 'relative',
    overflow: 'hidden',
    cursor: isDragging ? portrait ? 'ns-resize' : 'ew-resize' : undefined,
    userSelect: 'none',
    KhtmlUserSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none'
  }, style);

  return React.createElement("div", Object.assign({}, props, {
    ref: rootContainerRef,
    style: rootStyle,
    "data-rcs": "root"
  }), itemTwo, React.createElement(ReactCompareSliderClipContainer, {
    ref: clipContainerRef
  }, itemOne), React.createElement(ReactCompareSliderHandleContainer, {
    portrait: portrait,
    ref: handleContainerRef
  }, Handle));
};
/**
 * Image with fallback background for browsers that don't support the
 * `object-fit` CSS property.
 */


var ReactCompareSliderImage = function ReactCompareSliderImage(_ref) {
  var className = _ref.className,
      _ref$fallbackEnable = _ref.fallbackEnable,
      fallbackEnable = _ref$fallbackEnable === void 0 ? true : _ref$fallbackEnable,
      style = _ref.style,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "fallbackEnable", "style"]);
  /** Runtime support for `object-fit`. Ref based to allow updates after SSR. */


  var objectFitIsSupported = useRef(supportsCssObjectFit());
  var innerStyle = styleFitContainer(_extends({}, style));
  var containerStyle = {
    width: innerStyle.width,
    height: innerStyle.height,
    boxSizing: 'border-box'
  }; // Add fallback background props if requested

  if (!objectFitIsSupported.current && fallbackEnable) {
    // Set fallback CSS properties, use props from `innerStyle` if defined
    containerStyle.backgroundImage = innerStyle.backgroundImage || "url(" + props.src + ")";
    containerStyle.backgroundSize = innerStyle.backgroundSize || 'cover';
    containerStyle.backgroundPosition = innerStyle.backgroundPosition || 'center'; // Hide inner image

    innerStyle.opacity = 0;
  }

  return React.createElement("div", {
    className: className,
    style: containerStyle,
    "data-rcs": "image-root"
  }, React.createElement("img", Object.assign({}, props, {
    style: innerStyle,
    "data-rcs": "image-inner"
  })));
};

export { ReactCompareSlider, ReactCompareSliderHandle, ReactCompareSliderImage, styleFitContainer, supportsCssObjectFit };